import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../index.css';

// Typdefinitionen
interface ChatbotType {
    id: string;
    name: string;
    description: string;
    status: 'draft' | 'training' | 'ready' | 'live';
    createdAt: Date;
    lastUpdated: Date;
    interactions?: number;
    successRate?: number;
}

const Dashboard: React.FC = () => {
    // Mock-Daten für den Prototyp
    const [chatbots, setChatbots] = useState<ChatbotType[]>([
        {
            id: '1',
            name: 'Kundenservice Bot',
            description: 'Hilft Kunden bei häufigen Fragen und Problemen',
            status: 'live',
            createdAt: new Date('2025-04-15'),
            lastUpdated: new Date('2025-05-01'),
            interactions: 2453,
            successRate: 89
        },
        {
            id: '2',
            name: 'Produkt FAQ Bot',
            description: 'Beantwortet Fragen zu unseren Produkten',
            status: 'draft',
            createdAt: new Date('2025-05-05'),
            lastUpdated: new Date('2025-05-05'),
        },
        {
            id: '3',
            name: 'Sales Assistent',
            description: 'Unterstützt den Vertrieb bei der Lead-Generierung',
            status: 'training',
            createdAt: new Date('2025-05-02'),
            lastUpdated: new Date('2025-05-06'),
        },
    ]);

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Meine Chatbots</h1>
                <Link
                    to="/create"
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md flex items-center"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Neuen Chatbot erstellen
                </Link>
            </div>

            {/* Grid-Layout für Chatbot-Karten - nebeneinander */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {chatbots.map((chatbot) => (
                    <div
                        key={chatbot.id}
                        className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                    >
                        <div className="p-5">
                            <div className="flex justify-between items-start">
                                <h2 className="text-xl font-semibold text-gray-800 truncate">{chatbot.name}</h2>
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    chatbot.status === 'live' ? 'bg-green-100 text-green-800' :
                                        chatbot.status === 'ready' ? 'bg-blue-100 text-blue-800' :
                                            chatbot.status === 'training' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                }`}>
                  {chatbot.status === 'live' ? 'Live' :
                      chatbot.status === 'ready' ? 'Bereit' :
                          chatbot.status === 'training' ? 'Training' :
                              'Entwurf'}
                </span>
                            </div>

                            <p className="mt-2 text-gray-600 text-sm">{chatbot.description}</p>

                            {chatbot.interactions && (
                                <div className="mt-4 flex justify-between items-center text-sm text-gray-500">
                                    <span>{chatbot.interactions} Interaktionen</span>
                                    {chatbot.successRate && (
                                        <span className="font-medium text-green-600">{chatbot.successRate}% Erfolg</span>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-50 px-5 py-3 flex justify-between items-center">
              <span className="text-xs text-gray-500">
                Letzte Änderung: {chatbot.lastUpdated.toLocaleDateString()}
              </span>
                            <Link
                                to={`/chatbot/${chatbot.id}`}
                                className="text-sm font-medium text-blue-500 hover:text-blue-700"
                            >
                                {chatbot.status === 'draft' ? 'Fortsetzen' : 'Bearbeiten'}
                            </Link>
                        </div>
                    </div>
                ))}

                {/* "Neuen Chatbot erstellen" Karte */}
                <Link
                    to="/create"
                    className="bg-white rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 p-6 flex flex-col items-center justify-center text-center h-full"
                >
                    <div className="rounded-full bg-blue-100 p-3 mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">Neuen Chatbot erstellen</h3>
                    <p className="text-sm text-gray-500">Starte mit einem neuen KI-Assistenten für dein Unternehmen</p>
                </Link>
            </div>

            {chatbots.length === 0 && (
                <div className="text-center py-12">
                    <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">Keine Chatbots vorhanden</h3>
                    <p className="mt-1 text-sm text-gray-500">Erstelle deinen ersten Chatbot, um zu beginnen.</p>
                    <div className="mt-6">
                        <Link
                            to="/create"
                            className="px-4 py-2 bg-blue-500 hover:bg-blue-700 text-white rounded-md inline-flex items-center"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Neuen Chatbot erstellen
                        </Link>
                    </div>
                </div>
            )}

            {/* Analytics-Anzeige */}
            {chatbots.length > 0 && (
                <div className="mt-12">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Performance-Übersicht</h2>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="h-64 flex items-center justify-center text-gray-400 border border-dashed border-gray-300 rounded-md">
                            [Diagramm: Chatbot-Nutzung pro Tag wird hier angezeigt]
                        </div>

                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-lg font-medium text-gray-800 mb-3">Top Anfragen</h3>
                                <ul className="space-y-2">
                                    <li className="flex justify-between">
                                        <span className="text-gray-600">1. Öffnungszeiten</span>
                                        <span className="text-gray-500">22%</span>
                                    </li>
                                    <li className="flex justify-between">
                                        <span className="text-gray-600">2. Produktverfügbarkeit</span>
                                        <span className="text-gray-500">18%</span>
                                    </li>
                                    <li className="flex justify-between">
                                        <span className="text-gray-600">3. Bestellstatus</span>
                                        <span className="text-gray-500">15%</span>
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-800 mb-3">Ungelöste Anfragen</h3>
                                <ul className="space-y-2">
                                    <li className="text-gray-600">1. Rücksendung Status</li>
                                    <li className="text-gray-600">2. Rabattcode eingeben</li>
                                    <li className="text-gray-600">3. Lieferzeit ändern</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;